// src/components/AdminPortal/UserManagement/TenantApplications.js
import { useState, useEffect } from "react";
import Axios from "../../../utils/axios";
import { toast } from "react-toastify";
import { FiEdit, FiTrash2, FiPlus, FiX } from "react-icons/fi";
import "./TenantApplications.css";

const TenantApplications = () => {
  const [tenants, setTenants] = useState([]);
  const [applications, setApplications] = useState([]);
  const [tenantApplications, setTenantApplications] = useState([]);

  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedApp, setSelectedApp] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("Active");

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // -------------------- Fetch Data --------------------
  const fetchTenants = async () => {
    try {
      const res = await Axios.get(`/api/tenant/getAllTenants`);
      setTenants(res.data || []);
    } catch {
      toast.error("Failed to fetch tenants");
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await Axios.get(`/api/application/getAllApplications`);
      setApplications(res.data || []);
    } catch {
      toast.error("Failed to fetch applications");
    }
  };

  const fetchTenantAppsByTenantId = async (tenantId) => {
    if (!tenantId) {
      setTenantApplications([]);
      return;
    }

    try {
      const res = await Axios.get(
        `/api/tenantApplication/getAllApplicaationsByTenantId?tenantId=${tenantId}`
      );

      setTenantApplications(res.data || []);
    } catch {
      toast.error("Failed to fetch tenant applications");
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchApplications();
  }, []);

  useEffect(() => {
    fetchTenantAppsByTenantId(selectedTenant);
  }, [selectedTenant]);

  // -------------------- Save Handler --------------------
  const handleSave = async () => {
    if (!selectedTenant || !selectedApp) {
      toast.warning("Please select Tenant and Application");
      return;
    }

    const payload = {
      id: editingId || 0,
      tenantId: Number(selectedTenant),
      applicationId: Number(selectedApp),
      startDate: startDate || null,
      endDate: endDate || null,
      status,
    };

    setIsLoading(true);
    try {
      if (editingId) {
        await Axios.put(
          `/api/tenantApplication/updateTenantApplication?id=${editingId}`,
          payload
        );
      } else {
        await Axios.post(
          `/api/tenantApplication/createTenantApplication`,
          payload
        );
      }

      toast.success("Saved successfully");

      resetForm();
      fetchTenantAppsByTenantId(selectedTenant);
    } catch (err) {
      toast.error("Failed to save record");
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------- Edit --------------------
  const handleEdit = async (assoc) => {
    try {
      const res = await Axios.get(
        `/api/tenantApplication/getTenantApplicationById?id=${assoc.id}`
      );
      const data = res.data;

      setSelectedTenant(data.tenantId);
      setSelectedApp(data.applicationId);
      setStartDate(data.startDate || "");
      setEndDate(data.endDate || "");
      setStatus(data.status || "Active");

      setEditingId(data.id);
      setShowForm(true);
    } catch {
      toast.error("Failed to load record");
    }
  };

  // -------------------- Delete --------------------
  const confirmDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await Axios.delete(
        `/api/tenantApplication/deleteTenantApplication?id=${deleteTargetId}`
      );

      toast.success("Deleted");

      setShowDeleteModal(false);
      fetchTenantAppsByTenantId(selectedTenant);
    } catch {
      toast.error("Failed to delete");
    }
  };

  // -------------------- Reset Form --------------------
  const resetForm = () => {
    setSelectedApp("");
    setStartDate("");
    setEndDate("");
    setStatus("Active");
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="tenant-app-container">
      <div className="header-bar">
        <h2>Tenant Applications</h2>
        <button className="new-button" onClick={() => setShowForm(true)}>
          <FiPlus /> New
        </button>
      </div>

      {/* Tenant Dropdown */}
      <div className="form-field tenant-dropdown">
        <label>Select Tenant</label>
        <select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
        >
          <option value="">-- Select Tenant --</option>

          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.tenantName || t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content card">
            <div className="modal-header">
              <h3>
                {editingId
                  ? "Edit Tenant Application"
                  : "New Tenant Application"}
              </h3>
              <FiX className="close-icon" onClick={resetForm} />
            </div>

            <div className="form-section">
              <div className="form-field">
                <label>Select Application</label>
                <select
                  value={selectedApp}
                  onChange={(e) => setSelectedApp(e.target.value)}
                >
                  <option value="">-- Select Application --</option>

                  {applications.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.applicationName || app.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="action-buttons">
                <button className="save-button" onClick={handleSave}>
                  {isLoading ? "Saving..." : "Save"}
                </button>

                <button className="cancel-button" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {tenantApplications.length === 0 ? (
        <div className="loading">No tenant applications found</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {tenantApplications.map((assoc) => {
              const app = applications.find(
                (a) => a.id === assoc.applicationId
              );

              return (
                <tr key={assoc.id}>
                  <td>{app?.applicationName || app?.name || "Unknown"}</td>

                  <td>{assoc.startDate || "Not Set"}</td>
                  <td>{assoc.endDate || "Not Set"}</td>
                  <td>{assoc.status}</td>

                  <td>
                    <FiEdit
                      className="action-icon edit-icon"
                      onClick={() => handleEdit(assoc)}
                    />

                    <FiTrash2
                      className="action-icon delete-icon"
                      onClick={() => confirmDelete(assoc.id)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Delete Application</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleDeleteConfirmed();
              }}
            >
              <div className="form-group">
                <p>Are you sure you want to delete this application?</p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>

                <button type="submit" className="delete-button">
                  Delete
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantApplications;
